import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Switch,
  Typography,
  TextField,
  Button,
} from '@mui/material';
import AccuroSettingTable from './AccuroSettingTable';
import { useApiClient } from '../../../../context/ApiClientContext';
import AccuroNonEditableSettingTable from './AccuroNonEditableSettingTable';

export default function AccuroSettingPane(props) {
  const {
    entity,
    accuroSettings,
    onSave,
    setValues,
    refreshParent,
    setSuccessMessage,
    setErrorMessage,
  } = props;
  const [isEditingUUID, setIsEditingUUID] = useState(false);
  const [editValueUUID, setEditValueUUID] = useState('');
  const [availableIntakePlans, setAvailableIntakePlans] = useState(null);
  const apiClient = useApiClient();

  useEffect(() => {
    if (accuroSettings?.uuid?.value) {
      setEditValueUUID(accuroSettings.uuid.value);
    }
  }, [accuroSettings?.uuid?.value]);

  const getIntakePlans = async () => {
    try {
      const intakePlanList = (await apiClient.getIntakePlans()).data;
      return intakePlanList;
    } catch (error) {
      return [];
    }
  };

  useEffect(() => {
    if (!availableIntakePlans) {
      getIntakePlans().then((intakePlanList) => {
        const { intakePlans } = accuroSettings;
        const filteredPlans = intakePlanList.filter((plan) => intakePlans.value.split(',').includes(plan.id.toString()));
        setAvailableIntakePlans(filteredPlans);
      });
    }
  }, []);

  const handleSave = (setting, value) => {
    onSave(setting.name, setting.category, value);
  };

  return (
    <Box sx={{ margin: '16px' }}>
      <Grid
        container
        justifyContent="space-between"
      >
        <Grid
          item
          alignItems="column"
        >
          <Typography
            variant="body1"
          >
            Accuro Enabled
            <Switch
              checked={accuroSettings?.enabled?.value === 'true'}
              onChange={(event) => {
                handleSave(
                  accuroSettings.enabled,
                  event.target.checked.toString(),
                );
              }}
              name="Accuro enabled"
            />
          </Typography>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body1">UUID</Typography>
              {isEditingUUID ? (
                <TextField
                  size="small"
                  value={editValueUUID}
                  onChange={(e) => setEditValueUUID(e.target.value)}
                  fullWidth
                />
              ) : (
                <Typography variant="body1">{accuroSettings?.uuid?.value}</Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (isEditingUUID) {
                    handleSave(
                      accuroSettings.uuid,
                      editValueUUID,
                    );
                  }
                  setIsEditingUUID(!isEditingUUID);
                }}
              >
                {isEditingUUID ? 'Save' : 'Edit'}
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Grid
          item
          alignItems="column"
        >
          <Typography
            variant="body1"
          >
            Pre-Booked Automation
            <Switch
              checked={accuroSettings?.preBookedAutomationEnabled?.value === 'true'}
              onChange={(event) => {
                handleSave(
                  accuroSettings.preBookedAutomationEnabled,
                  event.target.checked.toString(),
                );
              }}
              name="Pre-Booked Automation"
            />
          </Typography>
          <Typography
            variant="body1"
          >
            Same-Day Automation
            <Switch
              checked={accuroSettings?.sameDayAutomationEnabled?.value === 'true'}
              onChange={(event) => {
                handleSave(
                  accuroSettings.sameDayAutomationEnabled,
                  event.target.checked.toString(),
                );
              }}
              name="Same-Day Automation"
            />
          </Typography>
        </Grid>
      </Grid>
      <AccuroSettingTable
        entity={entity}
        accuroSettings={accuroSettings}
        apiClient={apiClient}
        handleSave={handleSave}
        setValues={setValues}
        refreshParent={refreshParent}
        setSuccessMessage={setSuccessMessage}
        setErrorMessage={setErrorMessage}
      />
      <AccuroNonEditableSettingTable
        entity={entity}
        accuroSettings={accuroSettings}
        refreshParent={refreshParent}
        setSuccessMessage={setSuccessMessage}
        setErrorMessage={setErrorMessage}
      />
    </Box>
  );
}

// First, define a reusable setting shape
const settingShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

AccuroSettingPane.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    settings: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.string,
      value: PropTypes.string,
    })),
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  accuroSettings: PropTypes.shape({
    enabled: settingShape.isRequired,
    uuid: settingShape.isRequired,
    accessToken: settingShape.isRequired,
    refreshToken: settingShape.isRequired,
    accessTokenExpireTime: settingShape.isRequired,
    refreshTokenExpireTime: settingShape.isRequired,
    sentSMSStatus: settingShape.isRequired,
    notSentSMSStatus: settingShape.isRequired,
    intakeCompletedStatus: settingShape.isRequired,
    preBookedAutomationEnabled: settingShape.isRequired,
    sameDayAutomationEnabled: settingShape.isRequired,
    daysBeforeAppointment: settingShape.isRequired,
    folderId: settingShape.isRequired,
    subFolderId: settingShape.isRequired,
    appointmentType: settingShape.isRequired,
    sendDays: settingShape.isRequired,
    appointmentReasonIds: settingShape.isRequired,
    filterByProviderIds: settingShape.isRequired,
    providerIds: settingShape.isRequired,
    intakePlans: settingShape.isRequired,
  }).isRequired,
  setValues: PropTypes.func.isRequired,
  refreshParent: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};
